import { Contact, Customer, Location } from '../types/crm';
import { isSupabaseConfigured, supabase } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import {
  supabaseContactStorage,
  supabaseCustomerStorage,
  supabaseLocationStorage,
} from '../services/supabaseStorage';
import { deferCustomerWriteToOfflineIfNeeded } from '../services/offlineWriteQueue';
import { formatTitleCase, normalizeCustomerNameFields } from './textFormatting';

function customerDbDisplayName(customer: Customer): string {
  if (customer.customerType === 'company') {
    return (customer.companyName || '').trim() || 'Unnamed company';
  }
  return `${customer.firstName || ''} ${customer.surname || ''}`.trim() || 'Unnamed customer';
}

function locationHasAddressContent(loc: Location): boolean {
  return [loc.addressLine1, loc.addressLine2, loc.addressLine3, loc.city, loc.countyState, loc.eircodePostcode]
    .some((part) => String(part || '').trim().length > 0);
}

/** First location with address content; locations should already be ordered by created_at. */
export function getPrimaryLocation(customer?: Customer | null): Location | undefined {
  if (!customer) return undefined;
  const locations = customer.locations || [];
  return locations.find(locationHasAddressContent) || locations[0];
}

export function formatLocationOneLine(location?: Location): string {
  if (!location) return '';
  const parts: string[] = [];
  for (const part of [
    location.addressLine1,
    location.addressLine2,
    location.addressLine3,
    location.city,
    location.countyState,
    location.eircodePostcode,
    location.country,
  ]) {
    const trimmed = String(part || '').trim();
    if (!trimmed || trimmed === '—') continue;
    const normalized = trimmed.toLowerCase();
    const last = parts[parts.length - 1]?.toLowerCase();
    if (last === normalized) continue;
    parts.push(trimmed);
  }
  return parts.join(', ');
}

/** Parse a single-line address into street + city when possible. */
export function parseSingleLineAddress(raw: string): { addressLine1: string; city: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { addressLine1: '', city: '' };
  const commaParts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    return {
      addressLine1: commaParts.slice(0, -1).join(', '),
      city: commaParts[commaParts.length - 1] || '',
    };
  }
  return { addressLine1: trimmed, city: '' };
}

const EIRCODE_REGEX = /\b([A-Z]\d{2}\s?[A-Z0-9]{4})\b/i;
const COUNTY_REGEX = /^(?:Co\.|County)\s+(.+)$/i;
const COUNTRY_NAMES = new Set(['ireland', 'uk', 'united kingdom', 'poland', 'northern ireland']);

export interface ParsedMultilineAddress {
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  city: string;
  eircodePostcode?: string;
  countyState?: string;
  country: string;
}

/** Parse Sage multiline address blobs into structured location fields. */
export function parseMultilineAddress(raw: string): ParsedMultilineAddress | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const lines = trimmed
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) return null;

  let eircodePostcode: string | undefined;
  let countyState: string | undefined;
  let country = 'Ireland';
  const streetLines: string[] = [];

  for (const line of lines) {
    const eircodeMatch = line.match(EIRCODE_REGEX);
    if (eircodeMatch && !eircodePostcode) {
      eircodePostcode = eircodeMatch[1].toUpperCase().replace(/\s+/g, ' ').trim();
    }

    const countyMatch = line.match(COUNTY_REGEX);
    if (countyMatch) {
      countyState = countyMatch[1].trim();
      continue;
    }

    if (COUNTRY_NAMES.has(line.toLowerCase())) {
      country = line;
      continue;
    }

    if (eircodeMatch && line.replace(EIRCODE_REGEX, '').trim().length <= 2) {
      continue;
    }

    streetLines.push(line);
  }

  if (streetLines.length === 1 && streetLines[0].includes(',')) {
    const parsed = parseSingleLineAddress(streetLines[0]);
    return {
      addressLine1: parsed.addressLine1,
      city: parsed.city || '—',
      eircodePostcode,
      countyState,
      country,
    };
  }

  const city = streetLines.length > 1 ? streetLines[streetLines.length - 1] : '—';
  const addressParts = streetLines.length > 1 ? streetLines.slice(0, -1) : streetLines;

  return {
    addressLine1: addressParts[0] || streetLines[0] || '—',
    addressLine2: addressParts[1] || undefined,
    addressLine3: addressParts[2] || undefined,
    city: city || '—',
    eircodePostcode,
    countyState,
    country,
  };
}

/** Normalize company names for duplicate matching during imports. */
export function normalizeCompanyNameForMatching(name: string): string {
  if (!name || typeof name !== 'string') return '';

  let normalized = name.toLowerCase().trim();
  normalized = normalized.replace(/\s*\([^)]*\)\s*/g, ' ');
  normalized = normalized.replace(/[&/.,'"]/g, ' ');
  normalized = normalized.replace(/\s+/g, ' ');
  return normalized.trim();
}

function locationContentKey(location: Location): string {
  return [
    location.addressLine1,
    location.addressLine2,
    location.addressLine3,
    location.city,
    location.countyState,
    location.eircodePostcode,
    location.country,
  ]
    .map((part) => String(part || '').trim().toLowerCase())
    .filter((part) => part && part !== '—')
    .join('|');
}

/** Drop duplicate location rows (same address content). Keeps earliest by array order. */
export function dedupeCustomerLocations(locations: Location[]): Location[] {
  const seen = new Set<string>();
  const out: Location[] = [];
  for (const location of locations) {
    const key = locationContentKey(location) || location.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(location);
  }
  return out;
}

function customerToRpcPayload(customer: Customer): Record<string, unknown> {
  const c = normalizeCustomerNameFields(customer);
  return {
    id: c.id,
    tenant_id: DEFAULT_TENANT_ID,
    name: customerDbDisplayName(c),
    customer_type: c.customerType || 'company',
    company_role: c.companyRole || null,
    company_name: c.companyName,
    preferred_language: c.preferredLanguage || null,
    first_name: c.firstName || null,
    surname: c.surname || null,
    contact_phone_number: c.contactPhoneNumber || null,
    email_address: c.emailAddress || null,
    industry: c.industry || null,
    general_notes: c.generalNotes || null,
    sage_code: c.sageCode?.trim() || null,
    course_fee: c.courseFee ?? null,
    payment_on_the_day_type: c.paymentOnTheDayType || null,
    pre_payment_amount: c.prePaymentAmount ?? null,
    prepayment_payment_type: c.prepaymentPaymentType || null,
    booked_on: c.bookedOn || null,
    booked_by: c.bookedBy || null,
    mh_set_up: c.mhSetUp ?? null,
    invoice_number: c.invoiceNumber || null,
    other_info_notes: c.otherInfoNotes || null,
    ihasco_completed: c.ihascoCompleted ?? null,
    safe_pass_deposit_amount: c.safePassDepositAmount ?? null,
    safe_pass_deposit_type: c.safePassDepositType || null,
    safe_pass_deposit_date: c.safePassDepositDate || null,
    safe_pass_manual_handling: c.safePassManualHandling ?? null,
    safe_pass_process_number: c.safePassProcessNumber || null,
    safe_pass_card_posted_on: c.safePassCardPostedOn || null,
    safe_pass_post_to_address: c.safePassPostToAddress || null,
    safe_pass_card_status: c.safePassCardStatus || 'not_issued',
    num_certs_generated: c.numCertsGenerated ?? 0,
    is_paid: c.isPaid ?? false,
    last_payment_date: c.lastPaymentDate || null,
    payment_history: c.paymentHistory || [],
    current_batch_certificates: c.currentBatchCertificates || [],
    generated_certificates: c.generatedCertificates || [],
  };
}

function locationToRpcPayload(customerId: string, location: Location): Record<string, unknown> {
  const normalized = sanitizeLocationForDb(location as Location & Record<string, unknown>);
  return {
    id: normalized.id,
    tenant_id: DEFAULT_TENANT_ID,
    customer_id: customerId,
    address_line_1: normalized.addressLine1,
    address_line_2: normalized.addressLine2 || null,
    address_line_3: normalized.addressLine3 || null,
    city: normalized.city,
    eircode_postcode: normalized.eircodePostcode || null,
    county_state: normalized.countyState || null,
    country: normalized.country,
    location_contact_name: normalized.locationContactName || null,
    location_contact_email: normalized.locationContactEmail ?? null,
    location_contact_phone: normalized.locationContactPhone || null,
    location_notes: normalized.locationNotes || null,
  };
}

function contactToRpcPayload(customerId: string, contact: Contact): Record<string, unknown> {
  return {
    id: contact.id,
    tenant_id: DEFAULT_TENANT_ID,
    customer_id: customerId,
    first_name: contact.firstName,
    last_name: contact.lastName,
    role: contact.role || null,
    email: contact.email,
    phone_number: contact.phoneNumber || null,
    is_billing_contact: contact.isBillingContact ?? false,
    is_general_contact: contact.isGeneralContact ?? false,
    contact_notes: contact.contactNotes || null,
  };
}

export async function confirmCustomerPersisted(
  customerId: string,
  fallback?: Customer,
  maxAttempts = 10
): Promise<Customer> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const saved = await supabaseCustomerStorage.getCustomerById(customerId);
    if (saved) {
      return {
        ...saved,
        locations: dedupeCustomerLocations(saved.locations || []),
      };
    }
    await new Promise((resolve) => setTimeout(resolve, 120 * Math.pow(1.4, attempt)));
  }
  if (fallback) {
    return {
      ...fallback,
      locations: dedupeCustomerLocations(fallback.locations || []),
    };
  }
  throw new Error('Could not confirm saved customer record in the database.');
}

function isDuplicateKeyError(err: unknown): boolean {
  return (err as { code?: string })?.code === '23505';
}

async function locationExists(id: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  const { data, error } = await supabase.from('locations').select('id').eq('id', id).maybeSingle();
  if (error) throw error;
  return !!data;
}

async function contactExists(id: string): Promise<boolean> {
  if (!isSupabaseConfigured()) return false;
  const { data, error } = await supabase.from('contacts').select('id').eq('id', id).maybeSingle();
  if (error) throw error;
  return !!data;
}

/** Normalise location objects from AddEditCustomerModal into the DB Location shape. */
export function sanitizeLocationForDb(location: Location & Record<string, unknown>): Location {
  const addressLine1 = String(location.addressLine1 || '').trim();
  let city = String(location.city || location.cityTown || '').trim();
  if (city === '—') city = '';
  if (city && addressLine1 && city.toLowerCase() === addressLine1.toLowerCase()) {
    city = '';
  }
  return {
    id: location.id,
    addressLine1,
    addressLine2: location.addressLine2 || '',
    addressLine3: location.addressLine3 || '',
    city: city || '—',
    eircodePostcode: location.eircodePostcode || '',
    countyState: location.countyState || location.county || '',
    country: location.country || 'Ireland',
    locationContactName: location.locationContactName || undefined,
    locationContactEmail: location.locationContactEmail ?? null,
    locationContactPhone: location.locationContactPhone || '',
    locationNotes: location.locationNotes || '',
  };
}

export function clientDisplayEmail(customer: Customer): string {
  return (
    customer.emailAddress?.trim() ||
    customer.locations?.[0]?.locationContactEmail?.trim() ||
    customer.contacts?.find((c) => c.email?.trim())?.email?.trim() ||
    ''
  );
}

export function clientDisplayPhone(customer: Customer): string {
  return (
    customer.contactPhoneNumber?.trim() ||
    customer.locations?.[0]?.locationContactPhone?.trim() ||
    customer.contacts?.find((c) => c.phoneNumber?.trim())?.phoneNumber?.trim() ||
    ''
  );
}

export function individualCustomerDisplayName(customer: Customer): string {
  return `${customer.firstName || ''} ${customer.surname || ''}`.trim() || customer.companyName || 'Customer';
}

export function customerDisplayTitle(customer: Customer): string {
  if (customer.customerType === 'individual') {
    return individualCustomerDisplayName(customer);
  }
  return String(customer.companyName || '').trim() || individualCustomerDisplayName(customer) || 'Unnamed client';
}

/** Subtitle for session workspace “Add existing client” search hits. */
export function individualCustomerSearchSubtitle(customer: Customer): { languageLine: string | null; contactLine: string } {
  const name = individualCustomerDisplayName(customer);
  const lang = (customer.preferredLanguage || '').trim();
  const languageLine = lang ? `${name} – Prefers ${lang}` : null;
  const email = clientDisplayEmail(customer);
  const phone = clientDisplayPhone(customer);
  const contactLine = [email, phone].filter(Boolean).join(' – ') || 'No contact details';
  return { languageLine, contactLine };
}

function formatCompanyRoleLabel(role?: Customer['companyRole']): string | null {
  if (!role) return null;
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/** Title + subtitle for global search customer hits (matches session workspace individual layout). */
export function globalCustomerSearchDisplay(customer: Customer): { title: string; subtitle: string } {
  const sageCode = customer.sageCode?.trim() || null;
  const sageInTitle = sageCode ? ` · ${sageCode}` : '';

  if (customer.customerType === 'individual') {
    const displayName = individualCustomerDisplayName(customer);
    const { languageLine, contactLine } = individualCustomerSearchSubtitle(customer);
    return {
      title: `${languageLine || displayName}${sageInTitle}`,
      subtitle: contactLine,
    };
  }
  const name = String(customer.companyName || '').trim() || 'Unnamed client';
  const role = formatCompanyRoleLabel(customer.companyRole);
  const email = clientDisplayEmail(customer);
  const phone = clientDisplayPhone(customer);
  const contactLine = [email, phone].filter(Boolean).join(' – ') || 'No contact details';
  return {
    title: role ? `${name}${sageInTitle} / ${role}` : `${name}${sageInTitle}`,
    subtitle: contactLine,
  };
}

async function persistLocation(customerId: string, location: Location): Promise<void> {
  const normalized = sanitizeLocationForDb(location as Location & Record<string, unknown>);
  if (await locationExists(normalized.id)) {
    await supabaseLocationStorage.updateLocation(customerId, normalized);
    return;
  }
  try {
    await supabaseLocationStorage.addLocation(customerId, normalized);
  } catch (err) {
    if (isDuplicateKeyError(err)) {
      await supabaseLocationStorage.updateLocation(customerId, normalized);
      return;
    }
    throw err;
  }
}

async function persistContact(customerId: string, contact: Contact): Promise<void> {
  if (await contactExists(contact.id)) {
    await supabaseContactStorage.updateContact(customerId, contact);
    return;
  }
  try {
    await supabaseContactStorage.addContact(customerId, contact);
  } catch (err) {
    if (isDuplicateKeyError(err)) {
      await supabaseContactStorage.updateContact(customerId, contact);
      return;
    }
    throw err;
  }
}

export async function persistNewCustomer(customer: Customer): Promise<Customer> {
  const normalizedCustomer = normalizeCustomerNameFields(customer);
  if (await deferCustomerWriteToOfflineIfNeeded('create', normalizedCustomer)) {
    return normalizedCustomer;
  }
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured');
  }

  const existing = await supabaseCustomerStorage.getCustomerById(normalizedCustomer.id);
  if (existing) {
    return confirmCustomerPersisted(normalizedCustomer.id, existing);
  }

  const locations = dedupeCustomerLocations(normalizedCustomer.locations || []);
  const contacts = normalizedCustomer.contacts || [];
  const { error } = await supabase.rpc('create_customer_with_relations', {
    p_customer: customerToRpcPayload(normalizedCustomer),
    p_locations: locations.map((location) => locationToRpcPayload(normalizedCustomer.id, location)),
    p_contacts: contacts.map((contact) => contactToRpcPayload(normalizedCustomer.id, contact)),
  });
  if (error) {
    if (isDuplicateKeyError(error)) {
      return confirmCustomerPersisted(normalizedCustomer.id, normalizedCustomer);
    }
    throw error;
  }
  const { localCustomerStorage } = await import('../services/localStorageCache');
  localCustomerStorage.clear();
  return confirmCustomerPersisted(normalizedCustomer.id, {
    ...normalizedCustomer,
    locations,
    contacts,
  });
}

export async function persistCustomerProfileUpdate(customer: Customer): Promise<void> {
  const normalizedCustomer = normalizeCustomerNameFields(customer);
  if (await deferCustomerWriteToOfflineIfNeeded('update', normalizedCustomer)) {
    return;
  }
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured');
  }
  const locations = dedupeCustomerLocations(normalizedCustomer.locations || []);
  const contacts = normalizedCustomer.contacts || [];
  const { error } = await supabase.rpc('update_customer_with_relations', {
    p_customer: customerToRpcPayload(normalizedCustomer),
    p_locations: locations.map((location) => locationToRpcPayload(normalizedCustomer.id, location)),
    p_contacts: contacts.map((contact) => contactToRpcPayload(normalizedCustomer.id, contact)),
  });
  if (error) throw error;

  // Keep roster/booking labels in sync with CRM (enrollment names are snapshotted on book).
  if (normalizedCustomer.customerType === 'individual') {
    const firstName = (normalizedCustomer.firstName || '').trim() || null;
    const surname = (normalizedCustomer.surname || '').trim() || null;
    const { error: enrollError } = await supabase
      .from('course_session_attendees')
      .update({
        enrollment_first_name: firstName,
        enrollment_surname: surname,
      })
      .eq('individual_customer_id', normalizedCustomer.id);
    if (enrollError) {
      console.error('Failed to sync enrollment names after CRM update:', enrollError);
    }
  }

  const { localCustomerStorage } = await import('../services/localStorageCache');
  localCustomerStorage.clear();
}

export function buildCompanyContactFromForm(args: {
  contactName?: string;
  email?: string;
  phone?: string;
  existingContacts: Contact[];
}): Contact[] {
  const name = args.contactName?.trim();
  const email = args.email?.trim() || '';
  const phone = args.phone?.trim() || undefined;

  if (args.existingContacts.length > 0) {
    const [primary, ...rest] = args.existingContacts;
    if (!name) {
      return [{ ...primary, email, phoneNumber: phone }, ...rest];
    }
    const parts = name.split(/\s+/).filter(Boolean);
    const firstName = formatTitleCase(parts[0] || name);
    const lastName = formatTitleCase(parts.slice(1).join(' ') || parts[0] || name);
    return [
      {
        ...primary,
        firstName,
        lastName,
        email,
        phoneNumber: phone,
      },
      ...rest,
    ];
  }

  if (!name) return args.existingContacts;

  const parts = name.split(/\s+/).filter(Boolean);
  const firstName = formatTitleCase(parts[0] || name);
  const lastName = formatTitleCase(parts.slice(1).join(' ') || firstName);

  return [
    {
      id: crypto.randomUUID(),
      firstName,
      lastName,
      email,
      phoneNumber: phone,
      isBillingContact: true,
      isGeneralContact: true,
    },
  ];
}
